<script setup lang="ts">
import type { ConfiguratorPoolColor } from '~/models/configurator/configuratorPoolColor.model'

interface Props {
  selectableColors: ConfiguratorPoolColor[]
  shownColors: ConfiguratorPoolColor[]
}
const props = defineProps<Props>()
const { t } = useI18n()
const { dt } = useDirectusTranslation()
const currentColor = defineModel<ConfiguratorPoolColor>({
  required: true,
})

function isSelectable(color: ConfiguratorPoolColor): boolean {
  return props.selectableColors.some(s => s.id === color.id)
}

function isActive(color: ConfiguratorPoolColor): boolean {
  return currentColor.value.id === color.id
}

function onSelect(color: ConfiguratorPoolColor) {
  if (isSelectable(color))
    currentColor.value = color
}

const ORDER_COLORS: Record<string, number> = {
  'Inspiring White': 1,
  'Elegant Grey': 2,
  'Soothing Blue': 3,
  'Relaxing Blue': 4,
  'Stunning Grey': 5,
  'Amazing Graphite': 6,
}

function orderColors(colors: ConfiguratorPoolColor[]) {
  const clonedColors = colors.map(color => ({
    ...color,
  }))

  return clonedColors.sort((a, b) => {
    const aName = dt(a.translations)?.name ?? ''
    const bName = dt(b.translations)?.name ?? ''
    if (ORDER_COLORS[aName] === undefined)
      return aName.localeCompare(bName)
    if (ORDER_COLORS[bName] === undefined)
      return bName.localeCompare(aName)
    return ORDER_COLORS[aName] - ORDER_COLORS[bName]
  })
}

const orderenedColors = computed(() => orderColors(props.selectableColors))
</script>

<template>
  <div class="flex flex-col items-center gap-2">
    <h2 class="hidden text-3xl font-bold lg:block">
      {{ t('label.color') }}
    </h2>
    <p class="mb-2 h-6 text-1.3rem">
      {{ dt(currentColor.translations)?.name }}
    </p>
    <div class="w-48 flex flex-wrap items-center justify-center gap-2">
      <button
        v-for="color in orderenedColors" :key="color.id"
        :disabled="!isSelectable(color)"
        class="h-12 w-12 rounded-full"
        :class="{
          'opacity-50': !isSelectable(color),
          'border-2 ring-2 ring-starline-primary ring-offset-2 ring-offset-black': isActive(color),
        }"
        :style="`background-color: ${color.color}`"
        @click="onSelect(color)"
      >
        <div v-if="!isSelectable(color)" class="h-1 w-full bg-red-700 -rotate-45" />
      </button>
    </div>
  </div>
</template>
